import createFormJson from '../../demo/fixtures/create-form.json'
import planDraftJson from '../../demo/fixtures/plan-draft.json'
import { marketLabel } from '../../demo/store'
import type { Audience, Budget, CampaignDraft, Market, Objective } from '../../demo/types'

export type PlanDraftFieldKey = 'objective' | 'markets' | 'audience' | 'budget'

interface PlanDraftField {
  key: PlanDraftFieldKey
  label: string
  value: unknown
  display: string
  basis: string
  editable: boolean
}

interface PlanDraftFixture {
  meta: { productId: string; generatedBy: 'ai' }
  fields: PlanDraftField[]
  forecast: { metric: 'orders' | 'reach'; value: number; windowDays: number; basis: string }
}

export interface PlanDraftState {
  objective: Objective
  markets: Market[]
  audience: Audience
  budget: Budget
}

export const FORECAST_BY_OBJECTIVE = {
  sales: { metric: 'orders', value: 200, unit: '单' },
  awareness: { metric: 'reach', value: 500000, unit: '人' },
} as const satisfies Record<Objective, { metric: 'orders' | 'reach'; value: number; unit: string }>

function invariant(condition: unknown, message: string): asserts condition {
  if (!condition) throw new Error(`plan-draft.json 校验失败：${message}`)
}

const fixture = planDraftJson as unknown as PlanDraftFixture
const requiredKeys: PlanDraftFieldKey[] = ['objective', 'markets', 'audience', 'budget']
invariant(fixture.meta?.productId === 'p-mug' && fixture.meta.generatedBy === 'ai', 'meta 不合法')
invariant(Array.isArray(fixture.fields) && fixture.fields.length === requiredKeys.length, 'fields 必须正好四条')
const fieldKeys = fixture.fields.map((field) => field.key)
invariant(new Set(fieldKeys).size === requiredKeys.length && requiredKeys.every((key) => fieldKeys.includes(key)), '字段 key 缺失或重复')
invariant(fixture.fields.every((field) => field.editable === true), '四个字段必须都可调整')

function field<T>(key: PlanDraftFieldKey): PlanDraftField & { value: T } {
  const found = fixture.fields.find((item) => item.key === key)
  invariant(found, `缺少字段 ${key}`)
  return found as PlanDraftField & { value: T }
}

const objective = field<Objective>('objective').value
const markets = field<Market[]>('markets').value
const audienceValue = field<{ ageBands: string[]; interests: string[] }>('audience').value
const budgetValue = field<{ dailyMin: number; dailyMax: number }>('budget').value
invariant(objective === 'sales' || objective === 'awareness', 'objective 不在允许范围')
invariant(Array.isArray(markets) && markets.length > 0 && markets.every((item) => ['US', 'CA', 'GB', 'AU'].includes(item)), 'markets 不在允许范围')
invariant(Array.isArray(audienceValue.ageBands) && Array.isArray(audienceValue.interests), 'audience 形状不合法')
invariant(Number.isFinite(budgetValue.dailyMin) && Number.isFinite(budgetValue.dailyMax) && budgetValue.dailyMin < budgetValue.dailyMax, 'budget 不合法')
const expectedForecast = FORECAST_BY_OBJECTIVE[objective]
invariant(fixture.forecast.metric === expectedForecast.metric && fixture.forecast.value === expectedForecast.value, 'forecast 与目标口径不一致')

const interestKeys = audienceValue.interests.map((shortLabel) => {
  const match = createFormJson.customAudience.interests.find((item) => item.label.startsWith(shortLabel))
  invariant(match, `找不到兴趣标签 ${shortLabel}`)
  return match.key
})

export const PLAN_DRAFT = fixture

export const INITIAL_PLAN_DRAFT: PlanDraftState = {
  objective,
  markets: [...markets],
  audience: {
    type: 'custom',
    ageBands: [...audienceValue.ageBands],
    gender: createFormJson.customAudience.genders[0]?.key ?? 'all',
    interests: interestKeys,
  },
  budget: { ...budgetValue, total: 10000 },
}

export function displayPlanDraftField(key: PlanDraftFieldKey, draft: PlanDraftState): string {
  if (key === 'objective') return createFormJson.objectives.find((item) => item.key === draft.objective)?.label ?? draft.objective
  if (key === 'markets') return draft.markets.map(marketLabel).join('、')
  if (key === 'audience') {
    if (draft.audience.type === 'smart') return '智能推荐'
    const age = draft.audience.ageBands.map((value) => createFormJson.customAudience.ageBands.find((item) => item.key === value)?.label ?? value).join(' / ')
    const interests = draft.audience.interests.map((value) => createFormJson.customAudience.interests.find((item) => item.key === value)?.label ?? value).join(' / ')
    return [age, interests].filter(Boolean).join(' · ') || '自定义受众'
  }
  return `$${draft.budget.dailyMin}–$${draft.budget.dailyMax}`
}

export function toCampaignDraft(draft: PlanDraftState): CampaignDraft {
  return {
    objective: draft.objective,
    markets: draft.markets,
    audience: draft.audience,
    budget: draft.budget,
  }
}
